/** The Reachability card's model (plan "Page anatomy" → Reachability): one row per channel —
 *  Email · SMS · WhatsApp · Push · In-app chat — with the address that channel actually
 *  delivers to, the notifications opt-in, and the verdict in words. The verdicts and their
 *  reasons are the detail payload's (`reachability`, the delivery pipeline's own gates);
 *  nothing is re-derived here.
 *
 *  - `will_receive` → "Will receive" (ink);
 *  - `blocked` → "Blocked · <reason>" (the `error` icon and words — a gate stops delivery);
 *  - `unconfirmed` → "Unconfirmed · <reason>" (muted — nothing stops it, nothing confirms
 *    it). Never amber: amber is for pending edits. */
import type { ConsoleUserDetail, ReachabilityChannel, ReachabilityVerdict } from '@/api/users';
import {
  sameAddress,
  userAddressKey,
  type ContactsValue,
  type UserAddressType,
} from '@/features/records/fields/widgets/contactsModel';

export const REACHABILITY_CHANNELS: readonly ReachabilityChannel[] = ['email', 'sms', 'whatsapp', 'push', 'in_app_chat'];

export const CHANNEL_LABEL: Record<ReachabilityChannel, string> = {
  email: 'Email',
  sms: 'SMS',
  whatsapp: 'WhatsApp',
  push: 'Push',
  in_app_chat: 'In-app chat',
};

const VERDICT_WORD: Record<ReachabilityVerdict, string> = {
  will_receive: 'Will receive',
  blocked: 'Blocked',
  unconfirmed: 'Unconfirmed',
};

/** "Will receive", "Blocked · Opted out of SMS", "Unconfirmed · Goes through the OTA's relay…". */
export function verdictText(entry: { verdict: ReachabilityVerdict; reason: string | null }): string {
  const word = VERDICT_WORD[entry.verdict];
  return entry.verdict === 'will_receive' || !entry.reason ? word : `${word} · ${entry.reason}`;
}

export interface ChannelAddress {
  /** What delivery uses: an email, an E.164 number, the Main Office conversation. */
  value: string | null;
  kind: 'email' | 'phone' | 'conversation' | null;
  /** A muted note beside it ("for anna@bianchi.it", "archived", "expected: ACME-LIS"). */
  note: string | null;
}

const MAIN_OFFICE_STATE: Record<'active' | 'archived' | 'removed', string> = {
  active: 'active',
  archived: 'archived',
  removed: 'removed',
};

/** The address a channel delivers to, from the payload. Push has none (it goes to the
 *  app's chat identity), so nothing is shown for it. */
export function channelAddress(u: ConsoleUserDetail, channel: ReachabilityChannel): ChannelAddress {
  const r = u.reachability;
  switch (channel) {
    case 'email': {
      const a = r.email.address;
      const other = a && u.email && a.toLowerCase() !== u.email.toLowerCase();
      return { value: a, kind: a ? 'email' : null, note: other ? `for ${u.email}` : null };
    }
    case 'sms':
    case 'whatsapp': {
      const a = r[channel].address;
      return { value: a, kind: a ? 'phone' : null, note: null };
    }
    case 'in_app_chat': {
      const c = r.in_app_chat;
      const mo = c.main_office;
      if (!mo) return { value: null, kind: null, note: c.has_identity ? null : 'no chat identity' };
      const where = mo.tour_operator.code || mo.tour_operator.name;
      const expected =
        !mo.in_expected_channel && c.expected_tour_operator
          ? `expected: ${c.expected_tour_operator.code || c.expected_tour_operator.name}`
          : null;
      return {
        value: `Main Office · ${where}`,
        kind: 'conversation',
        note: [MAIN_OFFICE_STATE[mo.state], expected, c.has_identity ? null : 'no chat identity'].filter(Boolean).join(' · '),
      };
    }
    default:
      return { value: null, kind: null, note: null };
  }
}

/** The channel's notifications opt-in (null where the user has none to set). */
export function channelOptIn(u: ConsoleUserDetail, channel: ReachabilityChannel): boolean | null {
  switch (channel) {
    case 'email':
      return u.allow_email_nf;
    case 'sms':
      return u.allow_sms_nf;
    case 'whatsapp':
      return u.allow_whatsapp_nf;
    default:
      return null;
  }
}

/** Where a channel's address sits in the Contacts card: the contact row holding the address
 *  delivery uses (an OTA relay may be a contact of its own), else the user's own email /
 *  phone row, else the card's "not in contacts" row for the account's address. Null for a
 *  channel with no contact address (push, in-app chat) or nothing to deliver to. */
export type ChannelContact = { kind: 'row'; key: string } | { kind: 'missing'; type: UserAddressType };

export function channelContact(
  u: ConsoleUserDetail,
  channel: ReachabilityChannel,
  contacts: ContactsValue,
): ChannelContact | null {
  const type: UserAddressType | null = channel === 'email' ? 'email' : channel === 'sms' || channel === 'whatsapp' ? 'phone' : null;
  if (!type) return null;
  const address = channel === 'email' ? u.reachability.email.address : u.reachability[channel as 'sms' | 'whatsapp'].address;
  if (!address) return null;
  const exact = contacts.contacts.find((r) => r.type === type && sameAddress(type, r.contact, address));
  if (exact) return { kind: 'row', key: exact.key };
  const own = userAddressKey(contacts, type);
  if (own) return { kind: 'row', key: own };
  return (type === 'email' ? u.email : u.phone) ? { kind: 'missing', type } : null;
}
